import React, {useState} from "react";
import "focus-visible";
import {getFont, styled, useTheme, Theme} from "@core-ui/react-stitch";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import colorContrast from "color-contrast";
import {CSSObject} from "@emotion/react";
import {StockIndicator} from "./components/stock-indicator";
import {BackOrderMessage} from "./components/backorder-message";
import {ColorSwatchProps} from "./types";

const {useLocalize} =
  typeof __USE_I18N__ === "undefined"
    ? require("@core-ui/localization-provider")
    : require("@core-ui/i18n");

export const SMALL_COLOR_SWATCH_WIDTH = "1.125rem";
export const SMALL_COLOR_SWATCH_PADDING = "0.4375rem";

export const DEFAULT_COLOR_SWATCH_WIDTH = "1.875rem";
export const DEFAULT_COLOR_SWATCH_PADDING = "0.375rem";

export const getSwatchWidthInPx = (size: ColorSwatchProps["size"]): number => {
  const calculateWidth = (width: string, padding: string): number => {
    let pxToRemRatio = 16;
    if (window && document)
      pxToRemRatio = parseFloat(
        window.getComputedStyle(document.documentElement).fontSize
      );
    const DOUBLE_PADDING = 2;
    return (
      (parseFloat(width) + parseFloat(padding) * DOUBLE_PADDING) * pxToRemRatio
    );
  };

  if (size === "small")
    return calculateWidth(SMALL_COLOR_SWATCH_WIDTH, SMALL_COLOR_SWATCH_PADDING);

  return calculateWidth(
    DEFAULT_COLOR_SWATCH_WIDTH,
    DEFAULT_COLOR_SWATCH_PADDING
  );
};

const getInnerBoxShadowSpread = (size: ColorSwatchProps["size"]): string =>
  size === "small" ? "0.15rem" : "0.1875rem";

const getOuterBoxShadowSpread = (size: ColorSwatchProps["size"]): string =>
  size === "small" ? "0.28rem" : "0.375rem";

const getPadding = (size: ColorSwatchProps["size"]): string =>
  size === "small" ? SMALL_COLOR_SWATCH_PADDING : DEFAULT_COLOR_SWATCH_PADDING;

const SwatchWrapper = styled.div({
  display: "inline-block",
  position: "relative",
});

const SwatchAndLabelContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "@media (pointer: fine)": {
    "&:hover label": {
      textDecoration: "underline",
    },
  },
  "@media (pointer: coarse)": {
    "&:active label": {
      textDecoration: "underline",
    },
  },
});

const OutOfStockMessage = styled.span();

const SwatchContainer = styled.div<
  Required<Pick<ColorSwatchProps, "showColorName" | "size" | "selected">>
>(({size, selected, theme}) => {
  const height =
    size === "small" ? SMALL_COLOR_SWATCH_WIDTH : DEFAULT_COLOR_SWATCH_WIDTH;
  const width = height;

  return {
    display: "inline-block",
    position: "relative",
    height,
    width,
    textAlign: "center",
    verticalAlign: "top",
    whiteSpace: "inherit",
    padding: getPadding(size),
    ".focus-visible + img": {
      outline: 0,
      boxShadow: `0 0 0 ${getInnerBoxShadowSpread(size)} ${
        theme.crossBrand.color.b2
      }${
        selected
          ? `, 0 0 0 ${getOuterBoxShadowSpread(size)} ${theme.color.b1}`
          : ""
      }`,
      zIndex: -1,
    },
  };
});

const SwatchInput = styled.input({
  opacity: 0,
  position: "absolute",
  cursor: "pointer",
  width: "100%",
  height: "100%",
  zIndex: 1,
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  margin: 0,
});

const SwatchImage = styled.img<
  Required<Pick<ColorSwatchProps, "selected" | "size">>
>(({selected, size, theme}) => ({
  width: "100%",
  height: "100%",
  margin: 0,
  borderRadius: "50%",
  zIndex: 1,
  boxShadow: `${
    selected
      ? `0 0 0 ${getInnerBoxShadowSpread(size)} ${theme.color.wh},
  0 0 0 ${getOuterBoxShadowSpread(size)} ${theme.color.b1}`
      : ""
  }`,
}));

const getOutline = (
  customColor: CSSObject["background"],
  theme: Theme
): string => {
  try {
    if (colorContrast("white", customColor) < 1.3) {
      return `1px solid ${theme.color.gray05}`;
    }
    return "none";
  } catch (err) {
    return "none";
  }
};

const SwatchPlainColor = styled.div<
  Required<Pick<ColorSwatchProps, "selected" | "size" | "customColor">>
>(({customColor, selected, size, theme}) => ({
  width: "100%",
  height: "100%",
  margin: 0,
  borderRadius: "50%",
  zIndex: 1,
  background: customColor,
  border: getOutline(customColor, theme),
  boxShadow: `${
    selected
      ? `0 0 0 ${getInnerBoxShadowSpread(size)} ${theme.color.wh},
  0 0 0 ${getOuterBoxShadowSpread(size)} ${theme.color.b1}`
      : ""
  }`,
}));

const SwatchLabel = styled.label<{
  crossBrand?: boolean;
}>(({theme, crossBrand}) => ({
  ...getFont("primary")({theme, crossBrand}),
  paddingTop: "8px",
  fontSize: "0.875rem",
  textTransform: "capitalize",
}));

const shouldRenderStockIndicator = (
  shouldDisplayInStock: ColorSwatchProps["shouldDisplayInStock"],
  inStock: ColorSwatchProps["inStock"]
): boolean | undefined =>
  typeof inStock !== "undefined" && !inStock && shouldDisplayInStock;

export const ColorSwatch = ({
  backOrderMessage,
  className,
  colorName,
  crossBrand,
  customColor,
  id,
  inStock = true,
  onChange = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  radioName,
  productImage,
  productImageAltText,
  selected = false,
  shouldDisplayInStock = true,
  showColorName = false,
  size = "default",
  type = "radio",
  url,
  value,
  "aria-describedby": ariaDescribedByProp,
  ...props
}: ColorSwatchProps): JSX.Element => {
  const {localize} = useLocalize();
  const [hover, setHover] = useState(false);
  const theme = useTheme();
  const outOfStockId = `${id}-outofstock`;
  const outOfStock = localize("fui.color_swatch.out_of_stock", {});
  const isOutOfStock = shouldRenderStockIndicator(
    shouldDisplayInStock,
    inStock
  );

  const getAriaDescribedBy = (): string | undefined => {
    if (isOutOfStock && ariaDescribedByProp)
      return `${outOfStockId} ${ariaDescribedByProp}`;
    if (isOutOfStock) return outOfStockId;
    return ariaDescribedByProp;
  };

  return (
    <SwatchWrapper>
      <SwatchAndLabelContainer>
        <SwatchContainer
          className={className}
          selected={selected}
          showColorName={showColorName}
          size={size}
        >
          <SwatchInput
            aria-describedby={getAriaDescribedBy()}
            aria-disabled={isOutOfStock}
            aria-label={!showColorName ? colorName : undefined}
            checked={selected}
            data-product-image-alt-text={productImageAltText}
            data-product-image-src={productImage}
            id={id}
            name={radioName || "color-radio"}
            onChange={onChange}
            onMouseEnter={(event) => {
              onMouseEnter(event);
              setHover(true);
            }}
            onMouseLeave={(event) => {
              onMouseLeave(event);
              setHover(false);
            }}
            type={type}
            value={value}
            {...props}
          />
          {customColor ? (
            <SwatchPlainColor
              aria-hidden
              customColor={customColor}
              selected={selected}
              size={size}
              title={colorName}
            />
          ) : (
            <SwatchImage
              alt={colorName}
              aria-hidden
              selected={selected}
              size={size}
              src={url}
            />
          )}

          {isOutOfStock && <StockIndicator />}
          {backOrderMessage && (
            <BackOrderMessage
              aria-live="polite"
              hover={hover}
              role="alert"
              translateX={getOuterBoxShadowSpread(size)}
            >
              {backOrderMessage}
            </BackOrderMessage>
          )}
        </SwatchContainer>
        {showColorName && <SwatchLabel htmlFor={id}>{colorName}</SwatchLabel>}
        {isOutOfStock && (
          <OutOfStockMessage
            css={theme.utilities.visuallyHidden}
            id={outOfStockId}
          >
            {outOfStock}
          </OutOfStockMessage>
        )}
      </SwatchAndLabelContainer>
    </SwatchWrapper>
  );
};

export type {ColorSwatchProps} from "./types";
